import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { TrendingDown, Calculator, DollarSign, RefreshCw } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

const METHOD_LABELS = {
  last_purchase_price: 'Last Purchase Price',
  average_price: 'Average Price',
  budget_price: 'Budget / Estimated Price',
  first_bid: 'First Bid Received',
  manual: 'Manual Entry',
};

export function SavingsTab({ eventRef, form }) {
  const [loading, setLoading] = useState(true);
  const [savings, setSavings] = useState(null);
  const [savSort, handleSavSort] = useSortState('', 'asc');
  const [config, setConfig] = useState(null);
  const [method, setMethod] = useState('');
  const [calculating, setCalculating] = useState(false);
  const currency = form.currency || 'USD';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [savRes, cfgRes] = await Promise.all([
          api.get(`/api/savings/event/${eventRef}`).catch(() => ({ data: null })),
          api.get('/api/savings/config'),
        ]);
        if (savRes.data && savRes.data.event_ref) setSavings(savRes.data);
        setConfig(cfgRes.data);
        setMethod(form.savings_baseline?.method || cfgRes.data?.default_baseline_method || 'last_purchase_price');
      } catch { }
      setLoading(false);
    };
    load();
  }, [eventRef]); // eslint-disable-line

  const calculate = async () => {
    setCalculating(true);
    try {
      await api.post(`/api/savings/calculate/${eventRef}`, { baseline_method: method, line_items: [] });
      const r = await api.get(`/api/savings/event/${eventRef}`);
      setSavings(r.data);
      toast.success('Savings calculated');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Calculation failed')); }
    setCalculating(false);
  };

  const fmtMoney = (v) => `${currency} ${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

  if (loading) return <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>;

  return (
    <div className="space-y-4 py-2" data-testid="savings-tab">
      {/* Calculate controls */}
      <div className="border rounded-lg p-4 bg-emerald-50/50">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-4 h-4 text-emerald-600" />
          <h3 className="font-semibold text-sm">Calculate Savings</h3>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs">Baseline Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={calculate} disabled={calculating} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="calculate-savings-btn">
            {calculating ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Calculator className="w-3.5 h-3.5 mr-1" />}
            {calculating ? 'Calculating...' : savings ? 'Recalculate' : 'Calculate Savings'}
          </Button>
        </div>
        {config && !config.allow_event_override && method !== config.default_baseline_method && (
          <p className="text-xs text-red-500 mt-2">Your organisation does not allow overriding the default baseline method.</p>
        )}
      </div>

      {/* Results */}
      {savings ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-3">
            <div className="border rounded-lg p-3 text-center bg-white">
              <p className="text-xs text-slate-500 mb-1">Baseline</p>
              <p className="text-lg font-bold text-slate-700">{fmtMoney(savings.event_totals?.total_baseline)}</p>
            </div>
            <div className="border rounded-lg p-3 text-center bg-white">
              <p className="text-xs text-slate-500 mb-1">Awarded</p>
              <p className="text-lg font-bold text-slate-700">{fmtMoney(savings.event_totals?.total_awarded)}</p>
            </div>
            <div className="border rounded-lg p-3 text-center bg-emerald-50 border-emerald-200">
              <p className="text-xs text-emerald-600 mb-1">Savings</p>
              <p className="text-lg font-bold text-emerald-700">{fmtMoney(savings.event_totals?.saving_amount)}</p>
            </div>
            <div className="border rounded-lg p-3 text-center bg-emerald-50 border-emerald-200">
              <p className="text-xs text-emerald-600 mb-1">Savings %</p>
              <p className="text-lg font-bold text-emerald-700">{fmtPct(savings.event_totals?.saving_pct)}</p>
            </div>
          </div>

          {/* Baseline info */}
          <div className="flex items-center gap-4 text-xs text-slate-500 bg-slate-50 rounded-lg px-4 py-2">
            <span>Method: <strong>{METHOD_LABELS[savings.baseline_config?.method] || savings.baseline_config?.method}</strong></span>
            {savings.baseline_config?.is_override && <Badge className="text-[9px] bg-amber-100 text-amber-700">Override</Badge>}
            {savings.baseline_config?.justification && <span>Justification: {savings.baseline_config.justification}</span>}
            <span className="ml-auto">Calculated: {savings.calculated_at ? new Date(savings.calculated_at).toLocaleString() : ''}</span>
          </div>

          {/* Line items */}
          {savings.line_items?.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-100 border-b">
                  <SortableTableHeader label="Item" field="description" sortState={savSort} onSort={handleSavSort} />
                  <SortableTableHeader label="Qty" field="quantity" sortState={savSort} onSort={handleSavSort} className="text-right" />
                  <SortableTableHeader label={`Baseline (${currency})`} field="baseline_total" sortState={savSort} onSort={handleSavSort} className="text-right" />
                  <SortableTableHeader label={`Awarded (${currency})`} field="awarded_total" sortState={savSort} onSort={handleSavSort} className="text-right" />
                  <SortableTableHeader label={`Saving (${currency})`} field="saving_amount" sortState={savSort} onSort={handleSavSort} className="text-right" />
                  <SortableTableHeader label="%" field="saving_pct" sortState={savSort} onSort={handleSavSort} className="text-right" />
                  <SortableTableHeader label="Supplier" field="awarded_supplier" sortState={savSort} onSort={handleSavSort} />
                </tr></thead>
                <tbody>
                  {sortData(savings.line_items, savSort.field, savSort.dir).map((li, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-2">{li.description || li.item_id}</td>
                      <td className="px-3 py-2 text-right">{li.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono">{li.baseline_total?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{li.awarded_total?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">{li.saving_amount?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{fmtPct(li.saving_pct)}</td>
                      <td className="px-3 py-2">{li.awarded_supplier || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Supplier rollup */}
          {Object.keys(savings.supplier_rollup || {}).length > 0 && (
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-semibold mb-2">Savings by Supplier</h4>
              <div className="space-y-2">
                {Object.entries(savings.supplier_rollup).map(([code, data]) => (
                  <div key={code} className="flex items-center justify-between text-xs border-b pb-1.5 last:border-0">
                    <span className="font-medium">{code}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-slate-500">Baseline: {fmtMoney(data.total_baseline)}</span>
                      <span className="text-slate-500">Awarded: {fmtMoney(data.total_awarded)}</span>
                      <span className="font-semibold text-emerald-700">{fmtMoney(data.saving_amount)} ({fmtPct(data.saving_pct)})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cost avoidance */}
          {savings.cost_avoidance && savings.cost_avoidance.amount > 0 && (
            <div className="border rounded-lg p-4 bg-blue-50/50">
              <h4 className="text-sm font-semibold mb-1">Cost Avoidance</h4>
              <p className="text-lg font-bold text-blue-700">{fmtMoney(savings.cost_avoidance.amount)}</p>
              {savings.cost_avoidance.description && <p className="text-xs text-slate-500">{savings.cost_avoidance.description}</p>}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-slate-400">
          <TrendingDown className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No savings calculated yet. Use the button above to calculate.</p>
        </div>
      )}
    </div>
  );
}
